import { useEffect, useState } from 'react';
import { Room } from '@/data/rooms';
import { supabase } from '@/integrations/supabase/client';
import { getImageUrl } from '@/lib/image-url';

const staticRooms: Omit<Room, 'image' | 'images'>[] = [
  {
    id: 'salon',
    name: 'Pièce à vivre',
    type: 'living',
    description: 'Espace de vie principal lumineux comprenant un salon confortable, un coin repas convivial et un poêle pour vos soirées d\'hiver.',
    features: [
      { name: 'Poêle à granulés', icon: 'heater' },
      { name: 'Téléviseur connecté 55"', icon: 'tv' },
      { name: 'Grand canapé tout confort', icon: 'sofa' },
      { name: 'Grande table à manger', icon: 'table' },
    ],
  },
  {
    id: 'cuisine',
    name: 'Cuisine',
    type: 'kitchen',
    description: 'Cuisine moderne entièrement équipée, ouverte sur la terrasse pour des repas ensoleillés.',
    features: [
      { name: 'Four traditionnel & Micro-ondes', icon: 'chef-hat' },
      { name: 'Plaque vitrocéramique induction', icon: 'flame' },
      { name: 'Lave-vaisselle', icon: 'droplets' },
      { name: 'Réfrigérateur / Congélateur', icon: 'snowflake' },
    ],
  },
  {
    id: 'salle-de-bain',
    name: 'Salle de bain',
    type: 'bathroom',
    description: 'Salle de bain familiale spacieuse et moderne.',
    features: [
      { name: 'Grande douche 120x80cm', icon: 'droplets' },
      { name: 'Double vasque', icon: 'bath' },
      { name: 'Sèche-serviettes chauffant', icon: 'heater' },
    ],
  },
  {
    id: 'terrasse',
    name: 'Extérieurs & Terrasse',
    type: 'other',
    description: 'Espace extérieur privatif avec une grande terrasse en bois et un jardin clos pour la sécurité de vos enfants.',
    features: [
      { name: 'Terrasse en bois 75m²', icon: 'home' },
      { name: 'Barbecue à gaz professionnel', icon: 'flame' },
      { name: 'Jardin clos paysagé', icon: 'trees' },
      { name: 'Salon de jardin complet', icon: 'sofa' },
    ],
  },
];

const SECTION_MAP: Record<string, string> = {
  salon: 'room-sejour',
  cuisine: 'room-cuisine',
  'salle-de-bain': 'room-salle-de-bain',
  terrasse: 'room-terrasse',
};

export function useRooms(): Room[] {
  const [rows, setRows] = useState<Record<string, string[]>>({});

  useEffect(() => {
    const sections = Object.values(SECTION_MAP);
    supabase
      .from('site_images')
      .select('section, url, order')
      .in('section', sections)
      .order('order', { ascending: true })
      .then(({ data }) => {
        if (!data) return;
        const grouped: Record<string, string[]> = {};
        for (const row of data) {
          if (!grouped[row.section]) grouped[row.section] = [];
          grouped[row.section].push(getImageUrl(row.url));
        }
        setRows(grouped);
      });
  }, []);

  return staticRooms.map((r) => {
    const sectionKey = SECTION_MAP[r.id];
    const imgs = rows[sectionKey] ?? [];
    return { ...r, image: imgs[0] ?? '', images: imgs };
  });
}
